import React, { type FC, use, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Button, FieldError, InputGroup, Label, Spinner, TextField, toast } from "@heroui/react";
import { FormattedMessage, useIntl } from "react-intl";
import { ShieldCheck } from "@gravity-ui/icons";

import { AuthErrorCodes, signInWithEmailAndPassword } from "firebase/auth";

import { auth } from "@domain/api/firebase/firebase";
import { type LoginFormValues } from "./LoginForm";
import { UserContext } from "@domain/context/UserProvider";
import { logger } from "@domain/infrastructure/logger";
import { useValidationReactHookForm } from "@domain/infrastructure/helpers/validation";
import { getServerlessApi } from "@domain/api/backendApi";

interface Login2faFormValues {
    token: string;
}

const LOOSE_2FA_VALUES = { email: <a href="mailto:support@binale.de">support@binale.de</a> };

interface IProps {
    hide2fa: () => void;
    credentials: LoginFormValues | null;
}

export const Login2faForm: FC<IProps> = ({ hide2fa, credentials }) => {
    const intl = useIntl();
    const v = useValidationReactHookForm();
    const [errorMessageId, setErrorMessageId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const { isLoading: isUserLoading } = use(UserContext);

    const toggleHint = useCallback(() => setShowHint((s) => !s), []);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Login2faFormValues>();

    const onSubmit = async (values: Login2faFormValues) => {
        setLoading(true);
        try {
            const { host, headers } = await getServerlessApi();
            const res = await fetch(`${host}/auth/v1/authorize2fa`, {
                method: "POST",
                body: JSON.stringify({ data: { ...values, ...credentials } }),
                headers: { ...headers, "Content-Type": "application/json" },
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.error?.message ?? res.statusText);
            }
            await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
        } catch (e) {
            if (e.message === AuthErrorCodes.INVALID_MFA_SESSION) {
                setErrorMessageId("app.2fa.token_error");
            } else if (e.message === AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER) {
                setErrorMessageId("app.2fa.too_many_attempts");
            } else {
                logger.crit(new Error("Error with checking 2fa token"), { values, credentials, e });
                toast.danger(intl.formatMessage({ id: "app.global.error" }));
            }
        } finally {
            setLoading(false);
        }
    };

    const tokenReg = register("token", {
        required: v.required,
        maxLength: v.maxLength(6),
        minLength: v.minLength(6),
        pattern: v.onlyNumber,
    });

    return (
        <>
            {errorMessageId && (
                <Alert status="danger">
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>{intl.formatMessage({ id: errorMessageId })}</Alert.Title>
                    </Alert.Content>
                </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
                <TextField
                    isInvalid={!!errors.token}
                    {...tokenReg}
                    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- closes over per-render RHF register onChange identity; wrapping cannot stabilize it and would risk the controlled-field sync
                    onChange={(v: string) => {
                        tokenReg.onChange({ target: { value: v } });
                        setErrorMessageId(null);
                    }}
                >
                    <Label>
                        <FormattedMessage id="app.2fa.input_label" />
                    </Label>
                    <InputGroup>
                        <InputGroup.Input />
                        <InputGroup.Suffix>
                            <ShieldCheck className="text-muted size-4" />
                        </InputGroup.Suffix>
                    </InputGroup>

                    <FieldError>{errors.token?.message}</FieldError>
                </TextField>
                <Button type="submit" size="lg" isPending={loading || isUserLoading} fullWidth>
                    {({ isPending }) => (
                        <>
                            {isPending ? <Spinner color="current" size="sm" /> : null}
                            <FormattedMessage id="app.button.login" tagName="span" />
                        </>
                    )}
                </Button>
            </form>
            <div className="flex justify-between items-center">
                <Button variant="ghost" size="lg" onPress={hide2fa}>
                    <FormattedMessage id="app.global.previous_step" />
                </Button>
                <div className="text-accent underline decoration-dotted cursor-pointer" onClick={toggleHint}>
                    <FormattedMessage id="app.login.loose_2fa_app" />
                </div>
            </div>
            {showHint && (
                <div>
                    <FormattedMessage id="app.login.loose_2fa_app_instructions" values={LOOSE_2FA_VALUES} />
                </div>
            )}
        </>
    );
};
